import React, { useEffect, useMemo } from 'react';
import { useFieldArray } from 'react-hook-form';
import StepHeading, { SectionCard } from '../StepHeading';
import StepFooter from '../StepFooter';
import { useStepForm } from '../useStepForm';
import { TextInput, SelectInput, TextArea, DateInput, YesNoToggle, RepeatableGroup, ErrorSummary } from '../../ui/form';
import { todayIso } from '../../../utils/dateInput';

const emptyAccident = () => ({ date: '', description: '', location: '', fatalities: '0', injuries: '0', hazmatSpill: null, commercialVehicle: null });
const emptyViolation = () => ({ date: '', offense: '', location: '', vehicleType: '', penalty: '' });

const toFormValues = (application) => {
  const r = application.sections?.drivingRecord || {};
  const bool = (v) => (typeof v === 'boolean' ? v : null);
  return {
    drivingRecord: {
      accidents: { hadAny: bool(r.accidents?.hadAny), items: (r.accidents?.items || []).map((a) => ({ ...emptyAccident(), ...a, fatalities: String(a.fatalities ?? '0'), injuries: String(a.injuries ?? '0'), hazmatSpill: bool(a.hazmatSpill), commercialVehicle: bool(a.commercialVehicle) })) },
      violations: { hadAny: bool(r.violations?.hadAny), items: (r.violations?.items || []).map((v) => ({ ...emptyViolation(), ...v })) },
      forfeitures: { deniedLicense: bool(r.forfeitures?.deniedLicense), revokedOrSuspended: bool(r.forfeitures?.revokedOrSuspended), details: r.forfeitures?.details || '' }
    }
  };
};

const toPayload = (values) => {
  const r = values.drivingRecord || {};
  return {
    drivingRecord: {
      accidents: {
        hadAny: r.accidents?.hadAny,
        items: r.accidents?.hadAny ? (r.accidents.items || []).map((a) => ({ ...a, fatalities: a.fatalities === '' ? null : Number(a.fatalities), injuries: a.injuries === '' ? null : Number(a.injuries) })) : []
      },
      violations: { hadAny: r.violations?.hadAny, items: r.violations?.hadAny ? (r.violations.items || []) : [] },
      forfeitures: { deniedLicense: r.forfeitures?.deniedLicense, revokedOrSuspended: r.forfeitures?.revokedOrSuspended, details: r.forfeitures?.details || '' }
    }
  };
};

const LABELS = {
  'drivingRecord.accidents.hadAny': 'Accidents question', 'drivingRecord.accidents.items': 'Accident details',
  'drivingRecord.violations.hadAny': 'Violations question', 'drivingRecord.violations.items': 'Violation details',
  'drivingRecord.forfeitures.deniedLicense': 'License denied question', 'drivingRecord.forfeitures.revokedOrSuspended': 'License revoked question', 'drivingRecord.forfeitures.details': 'Circumstances',
  'drivingRecord.accidents.items.*.date': 'Accident {n}: date', 'drivingRecord.accidents.items.*.location': 'Accident {n}: location', 'drivingRecord.accidents.items.*.description': 'Accident {n}: description',
  'drivingRecord.accidents.items.*.fatalities': 'Accident {n}: fatalities', 'drivingRecord.accidents.items.*.injuries': 'Accident {n}: injuries',
  'drivingRecord.accidents.items.*.hazmatSpill': 'Accident {n}: hazmat spill', 'drivingRecord.accidents.items.*.commercialVehicle': 'Accident {n}: commercial vehicle',
  'drivingRecord.violations.items.*.date': 'Violation {n}: date', 'drivingRecord.violations.items.*.location': 'Violation {n}: location',
  'drivingRecord.violations.items.*.offense': 'Violation {n}: offense', 'drivingRecord.violations.items.*.vehicleType': 'Violation {n}: vehicle'
};

const DrivingRecordStep = ({ application, locked, flaggedMessage, onSaveDraft, onComplete, onContinue, onBack }) => {
  const defaultValues = useMemo(() => toFormValues(application), [application.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const today = todayIso();

  const validate = (values) => {
    const problems = [];
    const r = values.drivingRecord || {};
    if (r.accidents?.hadAny && !(r.accidents.items || []).length) problems.push({ name: 'drivingRecord.accidents.items', message: 'Add the details of each accident' });
    if (r.violations?.hadAny && !(r.violations.items || []).length) problems.push({ name: 'drivingRecord.violations.items', message: 'Add the details of each violation' });
    if ((r.forfeitures?.deniedLicense || r.forfeitures?.revokedOrSuspended) && !r.forfeitures?.details?.trim()) {
      problems.push({ name: 'drivingRecord.forfeitures.details', message: 'Briefly describe the circumstances' });
    }
    return problems;
  };

  const { form, values, errors, autosave, submitting, serverMessage, failedAttempts, submitNext } = useStepForm({
    stepId: 'record', defaultValues, toPayload, onSaveDraft, onComplete, locked, validate
  });
  const { register, control } = form;
  const accidents = useFieldArray({ control, name: 'drivingRecord.accidents.items' });
  const violations = useFieldArray({ control, name: 'drivingRecord.violations.items' });

  const hadAccidents = values.drivingRecord?.accidents?.hadAny;
  const hadViolations = values.drivingRecord?.violations?.hadAny;

  useEffect(() => {
    if (hadAccidents === true && accidents.fields.length === 0) accidents.append(emptyAccident());
    if (hadAccidents === false && accidents.fields.length > 0) accidents.replace([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hadAccidents]);

  useEffect(() => {
    if (hadViolations === true && violations.fields.length === 0) violations.append(emptyViolation());
    if (hadViolations === false && violations.fields.length > 0) violations.replace([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hadViolations]);

  const anyForfeiture = values.drivingRecord?.forfeitures?.deniedLicense || values.drivingRecord?.forfeitures?.revokedOrSuspended;

  return (
    <form onSubmit={(e) => { e.preventDefault(); submitNext(); }} noValidate>
      <StepHeading title="Driving Record" description="Accidents, moving violations, and license actions during the previous 3 years." flaggedMessage={flaggedMessage} locked={locked} />
      <ErrorSummary errors={errors} labels={LABELS} focusKey={failedAttempts} className="mb-5" />
      {serverMessage && !Object.keys(errors).length && <p className="mb-4 text-sm text-red-600" role="alert">{serverMessage}</p>}

      <fieldset disabled={locked} className="space-y-5">
        <SectionCard title="Accidents / crashes, previous 3 years">
          <YesNoToggle name="drivingRecord.accidents.hadAny" control={control} label="Have you had any accidents or crashes in the last 3 years?" required />
          {errors.drivingRecord?.accidents?.items?.message && <p className="mt-2 text-sm text-red-600" role="alert">{errors.drivingRecord.accidents.items.message}</p>}
          {hadAccidents === true && (
            <div className="mt-4">
              <RepeatableGroup
                items={accidents.fields}
                title={(i) => `Accident ${i + 1}`}
                addLabel="Add another accident"
                onAdd={() => accidents.append(emptyAccident())}
                onRemove={accidents.remove}
                minItems={1}
                disabled={locked}
                renderItem={(index) => {
                  const e = errors.drivingRecord?.accidents?.items?.[index] || {};
                  const base = `drivingRecord.accidents.items.${index}`;
                  return (
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                      <DateInput label="Date" required max={today} {...register(`${base}.date`, { required: 'Date is required' })} error={e.date?.message} />
                      <TextInput label="Location (city, state)" required {...register(`${base}.location`, { required: 'Location is required' })} error={e.location?.message} />
                      <TextArea label="Nature of the accident" required rows={2} className="md:col-span-2" placeholder="What happened? Who was involved?" {...register(`${base}.description`, { required: 'Describe what happened' })} error={e.description?.message} />
                      <TextInput label="Number of fatalities" required type="number" inputMode="numeric" min={0} {...register(`${base}.fatalities`, { required: 'Enter 0 if none' })} error={e.fatalities?.message} />
                      <TextInput label="Number of injuries" required type="number" inputMode="numeric" min={0} {...register(`${base}.injuries`, { required: 'Enter 0 if none' })} error={e.injuries?.message} />
                      <YesNoToggle name={`${base}.hazmatSpill`} control={control} label="Was there a hazardous materials spill?" required size="sm" />
                      <YesNoToggle name={`${base}.commercialVehicle`} control={control} label="Were you driving a commercial vehicle?" required size="sm" />
                    </div>
                  );
                }}
              />
            </div>
          )}
        </SectionCard>

        <SectionCard title="Moving traffic violations, previous 3 years" description="Convictions and forfeitures of bond or collateral, other than parking.">
          <YesNoToggle name="drivingRecord.violations.hadAny" control={control} label="Have you had any traffic violations in the last 3 years?" required />
          {errors.drivingRecord?.violations?.items?.message && <p className="mt-2 text-sm text-red-600" role="alert">{errors.drivingRecord.violations.items.message}</p>}
          {hadViolations === true && (
            <div className="mt-4">
              <RepeatableGroup
                items={violations.fields}
                title={(i) => `Violation ${i + 1}`}
                addLabel="Add another violation"
                onAdd={() => violations.append(emptyViolation())}
                onRemove={violations.remove}
                minItems={1}
                disabled={locked}
                renderItem={(index) => {
                  const e = errors.drivingRecord?.violations?.items?.[index] || {};
                  const base = `drivingRecord.violations.items.${index}`;
                  return (
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                      <DateInput label="Date" required max={today} {...register(`${base}.date`, { required: 'Date is required' })} error={e.date?.message} />
                      <TextInput label="Location (city, state)" required {...register(`${base}.location`, { required: 'Location is required' })} error={e.location?.message} />
                      <TextInput label="Offense" required placeholder="e.g. Speeding 10 over" {...register(`${base}.offense`, { required: 'Describe the offense' })} error={e.offense?.message} />
                      <SelectInput label="Vehicle" required options={[{ value: 'cmv', label: 'Commercial vehicle' }, { value: 'personal', label: 'Personal vehicle' }]} {...register(`${base}.vehicleType`, { required: 'Choose the vehicle type' })} error={e.vehicleType?.message} />
                      <TextInput label="Penalty" optionalLabel placeholder="Fine, points, etc." className="md:col-span-2" {...register(`${base}.penalty`)} />
                    </div>
                  );
                }}
              />
            </div>
          )}
        </SectionCard>

        <SectionCard title="License denials, suspensions and revocations">
          <div className="space-y-4">
            <YesNoToggle name="drivingRecord.forfeitures.deniedLicense" control={control} label="A. Have you ever been denied a license, permit or privilege to operate a motor vehicle?" required />
            <YesNoToggle name="drivingRecord.forfeitures.revokedOrSuspended" control={control} label="B. Has any license, permit or privilege ever been suspended or revoked?" required />
            <TextArea
              label="C. If yes to either question, briefly describe the circumstances"
              required={Boolean(anyForfeiture)}
              optionalLabel={!anyForfeiture}
              rows={3}
              {...register('drivingRecord.forfeitures.details')}
              error={errors.drivingRecord?.forfeitures?.details?.message}
            />
          </div>
        </SectionCard>
      </fieldset>

      <StepFooter onBack={onBack} onNext={locked ? onContinue : submitNext} isSubmitting={submitting} saveStatus={autosave.status} lastSavedAt={autosave.lastSavedAt} onRetrySave={autosave.saveNow} nextDisabled={locked && !onContinue} nextLabel={locked ? 'Continue' : 'Save & continue'} />
    </form>
  );
};

export default DrivingRecordStep;
